import { useState, useEffect, Fragment } from "react";
import {
  Plus, Search, Pencil, Trash2, ToggleLeft, ToggleRight,
  MapPin, Briefcase, Users, ChevronDown, ChevronUp, X,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_vacancy_data,
  manage_vacancy_status,
  delete_vacancy,
} from "../serviceconnection/ServiceConnection";
import VacancyForm from "./VacancyForm";

// ── Status Badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ val }) =>
  val === 1 ? (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: "hsl(var(--success-muted))", color: "hsl(var(--success))" }}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: "hsl(var(--destructive-muted))", color: "hsl(var(--destructive))" }}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" /> Inactive
    </span>
  );

// ─────────────────────────────────────────────────────────────────────────────
export default function Vacancies() {
  const [vacancies, setVacancies]       = useState([]);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [listLoading, setListLoading]   = useState(true);
  const [expandedRow, setExpandedRow]   = useState(null);
  const [deleteId, setDeleteId]         = useState(null);

  // { open: false } = list | { open: true, editData: null } = add | { open: true, editData: {...} } = edit
  const [formPage, setFormPage] = useState({ open: false, editData: null });

  // ── Derived ───────────────────────────────────────────────────────────────
  const filtered = vacancies.filter((v) => {
    const matchSearch =
      v.designation?.toLowerCase().includes(search.toLowerCase()) ||
      v.loaction?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "All" ||
      (statusFilter === "Active"   && v.status === 1) ||
      (statusFilter === "Inactive" && v.status !== 1);
    return matchSearch && matchStatus;
  });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchVacancies = async () => {
    try {
      setListLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      const res = await server_post_data(get_vacancy_data, fd);
      setVacancies(res.data?.data ?? []);
    } catch { showError("Failed to load vacancies"); }
    finally  { setListLoading(false); }
  };

  useEffect(() => { fetchVacancies(); }, []);

  // ── Toggle ────────────────────────────────────────────────────────────────
  const handleToggle = async (v) => {
    try {
      const fd = new FormData();
      fd.append("p_id",   v.primary_id);
      fd.append("status", v.status === 1 ? "0" : "1");
      const res = await server_post_data(manage_vacancy_status, fd);
      if (res.data?.error) { showError(res.data?.message || "Toggle failed"); return; }
      showSuccess(res.data?.message ?? "Status updated");
      setVacancies((prev) =>
        prev.map((item) =>
          item.primary_id === v.primary_id
            ? { ...item, status: v.status === 1 ? 0 : 1 }
            : item
        )
      );
    } catch { showError("Something went wrong"); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      const fd = new FormData();
      fd.append("p_id", deleteId);
      const res = await server_post_data(delete_vacancy, fd);
      if (res.data?.error) { showError(res.data?.message || "Delete failed"); return; }
      showSuccess("Vacancy deleted");
      setDeleteId(null);
      setVacancies((prev) => prev.filter((v) => v.primary_id !== deleteId));
    } catch { showError("Something went wrong"); }
  };

  // ── Form page ─────────────────────────────────────────────────────────────
  if (formPage.open) {
    return (
      <VacancyForm
        editData={formPage.editData}
        onBack={() => setFormPage({ open: false, editData: null })}
        onSaved={() => {
          setFormPage({ open: false, editData: null });
          fetchVacancies();
        }}
      />
    );
  }

  // ── List Page ─────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto space-y-5">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Vacancies</h1>
          <p className="page-subtitle">Manage open positions listed on the website</p>
        </div>
        <button
          onClick={() => setFormPage({ open: true, editData: null })}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white"
          style={{ background: "hsl(var(--accent))" }}>
          <Plus className="w-4 h-4" /> Add Vacancy
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: "Total",    value: vacancies.length,                              color: "hsl(var(--accent))"  },
          { label: "Active",   value: vacancies.filter((v) => v.status === 1).length, color: "hsl(var(--success))" },
          { label: "Inactive", value: vacancies.filter((v) => v.status !== 1).length, color: "hsl(var(--warning))" },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4">
            <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label} Vacancies</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="cms-card p-4 flex flex-wrap items-center gap-3">
        <div className="search-input flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by designation or location..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground" />
        </div>
        {["All", "Active", "Inactive"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              statusFilter === s ? "text-white border-transparent" : "border-border text-muted-foreground hover:border-accent/30"
            }`}
            style={statusFilter === s ? { background: "hsl(var(--accent))" } : {}}>
            {s}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="section-panel">
        <div className="overflow-x-auto">
          <table className="w-full cms-table">
            <thead>
              <tr>
                <th>Designation</th>
                <th>Location</th>
                <th>Positions</th>
                <th>Experience</th>
                <th>Salary</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm">No vacancies found</td></tr>
              ) : (
                filtered.map((v) => (
                  // ── Bug Fix: key Fragment pe, tr pe nahi ──────────────────
                  <Fragment key={v.primary_id}>
                    <tr className="group">
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ background: "hsl(var(--accent)/0.12)", color: "hsl(var(--accent))" }}>
                            <Briefcase className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-semibold text-foreground text-sm">{v.designation}</p>
                            <p className="text-xs text-muted-foreground">{v.qualification}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <MapPin className="w-3.5 h-3.5" />{v.loaction}
                        </span>
                      </td>
                      <td>
                        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Users className="w-3.5 h-3.5" />{v.no_of_position}
                        </span>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {v.min_experience}–{v.max_experience} yrs
                      </td>
                      <td className="text-sm text-foreground font-medium">{v.salary_details}</td>
                      <td><StatusBadge val={v.status} /></td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setExpandedRow(expandedRow === v.primary_id ? null : v.primary_id)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                            title="View Description">
                            {expandedRow === v.primary_id
                              ? <ChevronUp className="w-4 h-4" />
                              : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleToggle(v)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                            style={{ color: v.status === 1 ? "hsl(var(--success))" : "hsl(var(--warning))" }}>
                            {v.status === 1 ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          <button onClick={() => setFormPage({ open: true, editData: v })}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                            title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDeleteId(v.primary_id)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                            style={{ color: "hsl(var(--destructive))" }}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Description Row */}
                    {expandedRow === v.primary_id && (
                      <tr>
                        <td colSpan={7} className="bg-muted/40 px-4 py-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Job Description</p>
                              <p className="text-foreground whitespace-pre-wrap">{v.job_description}</p>
                            </div>
                            <div className="space-y-2">
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Skills</p>
                                <p className="text-foreground">{v.skills}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Qualification</p>
                                <p className="text-foreground">{v.qualification}</p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {vacancies.length} vacancies
          </p>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => setDeleteId(null)}>
          <div className="section-panel p-0 w-full max-w-sm mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}>
            <div className="section-panel-header">
              <h2 className="section-panel-title">Delete Vacancy</h2>
            </div>
            <div className="section-panel-body space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete this vacancy? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeleteId(null)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted">
                  Cancel
                </button>
                <button onClick={handleDelete}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: "hsl(var(--destructive))" }}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}